import React from "react";
import { Building2, Check, Copy, ExternalLink, Globe, KeyRound, Mail, MapPin, Phone, Plus, Shield, UserPlus, Users } from "lucide-react";
import { TitleToolbar } from "@core/components/TitleToolbar";
import { useOrganization } from "@core/organizations/OrganizationProvider";
import { getPublicCatalogueUrl } from "@core/utils/subdomain";

export function BusinessSettingsPage() {
  const { organizations, activeOrganization, selectOrganization, updateBusinessProfile, listMembers, addMember, can } = useOrganization();
  const [profile, setProfile] = React.useState({
    name: activeOrganization?.name || "",
    shortName: activeOrganization?.shortName || "",
    slug: activeOrganization?.slug || "nwautosupreme",
    phone: activeOrganization?.phone || "(206) 555-0199",
    email: activeOrganization?.email || "service@nwautosupreme.com",
    website: activeOrganization?.website || "https://nwautosupreme.com",
    address: activeOrganization?.address || "14820 Highway 99",
    city: activeOrganization?.city || "Lynnwood",
    state: activeOrganization?.state || "WA",
    zip: activeOrganization?.zip || "98087",
    tagline: activeOrganization?.tagline || "Precision Dealership & Retail Recon Specialists",
    description: activeOrganization?.description || "Mobile and bay automotive restyling, ceramic window tint, paint protection film, and interior reconditioning.",
  });

  const [savingProfile, setSavingProfile] = React.useState(false);
  const [profileNotice, setProfileNotice] = React.useState("");
  const [copiedLink, setCopiedLink] = React.useState(false);

  const [members, setMembers] = React.useState([]);
  const [loadingMembers, setLoadingMembers] = React.useState(true);
  const [addMemberOpen, setAddMemberOpen] = React.useState(false);
  const [newMember, setNewMember] = React.useState({ displayName: "", email: "", role: "technician" });
  const [addingMember, setAddingMember] = React.useState(false);
  const [memberNotice, setMemberNotice] = React.useState("");

  React.useEffect(() => {
    if (activeOrganization) {
      setProfile({
        name: activeOrganization.name || "",
        shortName: activeOrganization.shortName || "",
        slug: activeOrganization.slug || (activeOrganization.name || "").toLowerCase().replace(/[^a-z0-9]/g, "") || "nwautosupreme",
        phone: activeOrganization.phone || "",
        email: activeOrganization.email || "",
        website: activeOrganization.website || "",
        address: activeOrganization.address || "",
        city: activeOrganization.city || "",
        state: activeOrganization.state || "",
        zip: activeOrganization.zip || "",
        tagline: activeOrganization.tagline || "",
        description: activeOrganization.description || "",
      });
    }
  }, [activeOrganization]);

  React.useEffect(() => {
    let cancelled = false;
    setLoadingMembers(true);
    listMembers()
      .then((items) => { if (!cancelled) setMembers(items); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingMembers(false); });
    return () => { cancelled = true; };
  }, [activeOrganization?.id, listMembers]);

  const publicCatalogueUrl = getPublicCatalogueUrl(profile.slug || "nwautosupreme");

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(publicCatalogueUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch {
      // fallback
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!can("organizations.manage") && activeOrganization?.role !== "owner") {
      setProfileNotice("You do not have permission to edit business details.");
      return;
    }
    setSavingProfile(true);
    setProfileNotice("");
    try {
      await updateBusinessProfile(profile);
      setProfileNotice("Business details saved successfully.");
      setTimeout(() => setProfileNotice(""), 3500);
    } catch (err) {
      setProfileNotice(err instanceof Error ? err.message : "Could not save profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    setAddingMember(true);
    setMemberNotice("");
    try {
      const added = await addMember(newMember);
      setMembers((prev) => [...prev.filter((m) => m.email !== newMember.email), added]);
      setNewMember({ displayName: "", email: "", role: "technician" });
      setAddMemberOpen(false);
      setMemberNotice(`Added ${added.displayName || added.email} as ${added.role}.`);
      setTimeout(() => setMemberNotice(""), 3500);
    } catch (err) {
      setMemberNotice(err instanceof Error ? err.message : "Could not add user.");
    } finally {
      setAddingMember(false);
    }
  };

  return (
    <div className="settings-page-stack">
      <TitleToolbar
        density="compact"
        title="Business & workspace"
        icon={Building2}
      />

      {/* Public Sharing Link Card */}
      <section className="settings-card share-link-banner">
        <div className="settings-card-header">
          <span className="settings-list-icon"><Globe /></span>
          <div>
            <h2>Public catalogue & customer sharing link</h2>
            <p>Customers and dealerships can visit your dedicated shop subdomain to browse services and request work.</p>
          </div>
        </div>
        <div className="public-link-box">
          <div className="public-url-display">
            <code>{publicCatalogueUrl}</code>
          </div>
          <div className="public-url-actions">
            <button className="button button-secondary" type="button" onClick={copyShareLink}>
              {copiedLink ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
              <span>{copiedLink ? "Copied" : "Copy link"}</span>
            </button>
            <a className="button button-primary" href={publicCatalogueUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={14} />
              <span>Open catalogue</span>
            </a>
          </div>
        </div>
      </section>

      {/* Business Details Editor */}
      <section className="settings-card">
        <div className="settings-card-header">
          <span className="settings-list-icon"><Building2 /></span>
          <div>
            <h2>Business details</h2>
            <p>Your shop contact details, branding, address, and subdomain handle.</p>
          </div>
        </div>

        <form className="business-details-form" onSubmit={handleSaveProfile}>
          <div className="form-grid-2">
            <label className="form-field">
              <span>Business name</span>
              <input name="name" value={profile.name} onChange={handleProfileChange} placeholder="e.g. NW Auto Supreme" required />
            </label>
            <label className="form-field">
              <span>Subdomain handle / URL slug</span>
              <input name="slug" value={profile.slug} onChange={handleProfileChange} placeholder="e.g. nwautosupreme" required pattern="[a-z0-9-_]+" />
              <small className="field-hint">Public link: {profile.slug || "yourshop"}.venpro.app</small>
            </label>
          </div>

          <div className="form-grid-3">
            <label className="form-field">
              <span><Phone size={13} className="inline-icon" /> Phone number</span>
              <input name="phone" value={profile.phone} onChange={handleProfileChange} placeholder="(555) 000-0000" />
            </label>
            <label className="form-field">
              <span><Mail size={13} className="inline-icon" /> Work email</span>
              <input name="email" type="email" value={profile.email} onChange={handleProfileChange} placeholder="contact@shop.com" />
            </label>
            <label className="form-field">
              <span><Globe size={13} className="inline-icon" /> Website</span>
              <input name="website" value={profile.website} onChange={handleProfileChange} placeholder="https://shop.com" />
            </label>
          </div>

          <div className="form-grid-address">
            <label className="form-field address-street">
              <span><MapPin size={13} className="inline-icon" /> Street address</span>
              <input name="address" value={profile.address} onChange={handleProfileChange} placeholder="123 Main St" />
            </label>
            <label className="form-field">
              <span>City</span>
              <input name="city" value={profile.city} onChange={handleProfileChange} placeholder="City" />
            </label>
            <label className="form-field state-field">
              <span>State</span>
              <input name="state" value={profile.state} onChange={handleProfileChange} placeholder="WA" maxLength={4} />
            </label>
            <label className="form-field zip-field">
              <span>ZIP code</span>
              <input name="zip" value={profile.zip} onChange={handleProfileChange} placeholder="98101" maxLength={10} />
            </label>
          </div>

          <label className="form-field">
            <span>Tagline / specialty</span>
            <input name="tagline" value={profile.tagline} onChange={handleProfileChange} placeholder="e.g. Dealership Restyling & Ceramic Tint Specialists" />
          </label>

          <label className="form-field">
            <span>Shop description (shown on public catalogue)</span>
            <textarea name="description" rows={3} value={profile.description} onChange={handleProfileChange} placeholder="Describe services offered, warranty info, shop location..." />
          </label>

          <div className="form-footer">
            {profileNotice && <span className="profile-notice">{profileNotice}</span>}
            <button className="button button-primary" type="submit" disabled={savingProfile}>
              {savingProfile ? "Saving..." : "Save business details"}
            </button>
          </div>
        </form>
      </section>

      {/* Team Users Management */}
      <section className="settings-card">
        <div className="settings-card-header justify-between">
          <div className="flex items-center gap-3">
            <span className="settings-list-icon"><Users /></span>
            <div>
              <h2>Team & users</h2>
              <p>Staff members, technicians, dispatchers, and managers who have access to this workspace.</p>
            </div>
          </div>
          <button className="button button-primary" type="button" onClick={() => setAddMemberOpen(true)}>
            <UserPlus size={15} />
            <span>Add member</span>
          </button>
        </div>

        {memberNotice && <div className="p-3 mb-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg text-xs font-semibold">{memberNotice}</div>}

        {addMemberOpen && (
          <form className="add-member-drawer" onSubmit={handleAddMember}>
            <div className="add-member-header">
              <h3><UserPlus size={16} /> Add team member</h3>
              <button type="button" className="close-btn" onClick={() => setAddMemberOpen(false)}>×</button>
            </div>
            <div className="form-grid-3">
              <label className="form-field">
                <span>Full name</span>
                <input required value={newMember.displayName} onChange={(e) => setNewMember((prev) => ({ ...prev, displayName: e.target.value }))} placeholder="e.g. John Doe" />
              </label>
              <label className="form-field">
                <span>Email address</span>
                <input required type="email" value={newMember.email} onChange={(e) => setNewMember((prev) => ({ ...prev, email: e.target.value }))} placeholder="technician@shop.com" />
              </label>
              <label className="form-field">
                <span>Role</span>
                <select value={newMember.role} onChange={(e) => setNewMember((prev) => ({ ...prev, role: e.target.value }))}>
                  <option value="technician">Technician (Jobs, scanning, photos)</option>
                  <option value="dispatcher">Dispatcher (Scheduling, customer contact, jobs)</option>
                  <option value="manager">Manager (Catalogue, invoices, team, jobs)</option>
                  <option value="admin">Administrator (Full business access)</option>
                </select>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button className="button button-secondary" type="button" onClick={() => setAddMemberOpen(false)}>Cancel</button>
              <button className="button button-primary" type="submit" disabled={addingMember}>
                {addingMember ? "Adding..." : "Add to team"}
              </button>
            </div>
          </form>
        )}

        <div className="members-table-container">
          {loadingMembers ? (
            <div className="p-4 text-xs text-gray-500">Loading team members...</div>
          ) : members.length === 0 ? (
            <div className="p-4 text-xs text-gray-500">No members found. Add team members above.</div>
          ) : (
            <table className="members-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.id || member.userId || member.email}>
                    <td>
                      <strong>{member.displayName || member.email?.split("@")[0]}</strong>
                      {member.isOwner && <span className="owner-badge">Owner</span>}
                    </td>
                    <td><small className="text-gray-500">{member.email}</small></td>
                    <td>
                      <span className={`role-pill role-${member.role || "staff"}`}>
                        {member.role === "staff" ? "Technician" : member.role === "office" ? "Dispatcher" : member.role}
                      </span>
                    </td>
                    <td>
                      <span className="status-dot-active">● Active</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Switch Organizations */}
      <section className="settings-card">
        <div className="settings-card-header">
          <span className="settings-list-icon"><Building2 /></span>
          <div><h2>Active business</h2><p>Switch between organizations you belong to.</p></div>
        </div>
        <div className="organization-card-list">
          {organizations.map((organization) => {
            const active = organization.id === activeOrganization.id;
            return (
              <button key={organization.id} type="button" className={active ? "is-active" : ""} onClick={() => selectOrganization(organization.id)}>
                <Building2 aria-hidden="true" />
                <span><strong>{organization.name}</strong><small>{organization.role.replaceAll("_", " ")}</small></span>
                {active && <Check aria-label="Active" />}
              </button>
            );
          })}
        </div>
      </section>

      {/* Effective Permissions */}
      <section className="settings-card">
        <div className="settings-card-header">
          <span className="settings-list-icon"><KeyRound /></span>
          <div><h2>Effective permissions</h2><p>Actions your current role can perform.</p></div>
        </div>
        <div className="permission-chip-list">
          {(activeOrganization?.permissions || []).map((permission) => <span key={permission}>{permission}</span>)}
        </div>
      </section>
    </div>
  );
}

